import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../app/authStore';
import { ApiError, API_BASE, apiUrl, describeError, isApiPath } from '../lib/api';
import { usePlatformFlags } from '../lib/platformFlags';
import { Button } from '../components/ui';
import { Field, Input, PasswordInput, Checkbox, Notice } from '../components/form';
import { Icon } from '../components/Icon';

/**
 * 登录页。
 *
 * 规格照阿里云登录页的实拍取数：左侧渐变营销区（主标题 50px/600、副标题 21px/600），
 * 右侧 400px 白卡；**输入框与主按钮都是直角、高 40px、内文 14px**，
 * 主按钮是满宽的实心 `#1366EC`。
 *
 * 两步验证走同一条链路：口令提交后服务端返回 `totp_required`，再就地展开验证码输入，
 * 而不是先让用户猜「我到底该填哪种表单」。
 */
export function LoginPage() {
  const login = useAuthStore((state) => state.login);
  const loginWithRecoveryCode = useAuthStore((state) => state.loginWithRecoveryCode);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { flags } = usePlatformFlags();
  /** 运营关闭登录后禁用提交：所有人都会被强制下线，留着可点的按钮只会制造困惑。 */
  const loginClosed = !flags.loginEnabled;

  const [tab, setTab] = useState<'password' | 'recovery'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [remember, setRemember] = useState(false);

  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const continueTo = params.get('continue') ?? '/';
  const gate = params.get('gate');

  /**
   * 登录成功后回到 `continue` 指向的地方。三档：
   *   1. 还必须改密 → 先去改密页，并把 continue 带上，改完再接着走；
   *   2. continue 是接口路径（OIDC 的 `/authorize?...`）→ 整页跳到接口域名，
   *      用前端路由器导航只会落到 console 自己的同名路径上，然后又变成未登录；
   *   3. 其余（SPA 内部路径）→ 交给路由器。
   */
  function goContinue() {
    if (useAuthStore.getState().session?.mustChangePassword) {
      navigate(`/password?continue=${encodeURIComponent(continueTo)}`, { replace: true });
      return;
    }
    if (API_BASE !== '' && isApiPath(continueTo)) {
      window.location.replace(apiUrl(continueTo));
      return;
    }
    navigate(continueTo, { replace: true });
  }

  function fail(error: unknown) {
    if (error instanceof ApiError) {
      setFieldErrors(error.fieldErrors);
      // `totp_required` 不是错误，而是「还差一步」。
      if (error.code === 'totp_required') {
        setNeedsCode(true);
        setError('请输入两步验证码');
        return;
      }
      setError(error.message);
      return;
    }
    setError(describeError(error));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      if (tab === 'recovery') {
        await loginWithRecoveryCode(email.trim(), password, recoveryCode.trim());
      } else {
        await login(email.trim(), password, { code: code.trim(), remember });
      }
      goContinue();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full bg-canvas">
      {/* ------------------------------------------------- 左侧渐变营销区 */}
      <section className="login-marketing hidden flex-1 flex-col justify-center px-12 lg:flex xl:px-20">
        <h1 className="text-[50px] leading-[70px] font-semibold text-fg">
          AI 与低空，
          <br />
          交给{' '}
          <span className="bg-gradient-to-r from-[#bc4bed] to-[#12bbfd] bg-clip-text text-transparent">
            Havorix
          </span>
        </h1>
        <p className="mt-4 text-[21px] font-semibold text-fg">无人机的运营与监管，集中在一个控制台</p>
        <ul className="mt-8 space-y-2 text-[13px] text-fg-muted">
          {[
            '账号、成员与权限统一管理',
            'OIDC 单点登录，一次接入处处可用',
            '登录历史与操作记录完整留痕',
          ].map((line) => (
            <li key={line} className="flex items-center gap-2">
              <Icon name="check" className="h-4 w-4 text-brand" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      {/* --------------------------------------------------- 右侧登录白卡 */}
      <section className="flex w-full items-center justify-center px-4 py-10 sm:px-6 lg:w-[600px]">
        <div className="w-full max-w-[400px]">
          <div className="mb-6 lg:hidden">
            <span className="text-[15px] font-semibold text-fg">Havorix</span>
            <span className="text-[11px] text-fg-subtle">™</span>
          </div>

          <h1 className="mb-6 text-[34px] leading-[44px] font-semibold text-fg">登录</h1>

          <div className="mb-5 flex gap-6 text-base">
            {(
              [
                ['password', '账号密码'],
                ['recovery', '恢复码登录'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setTab(id);
                  setError(null);
                  setNeedsCode(false);
                }}
                className={`transition-colors ${
                  tab === id ? 'font-semibold text-brand' : 'font-normal text-fg-subtle hover:text-fg-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {gate === 'password_change_required' ? (
            <div className="mb-4">
              <Notice tone="warn">当前账号仍在使用初始口令，请先登录并修改口令。</Notice>
            </div>
          ) : null}

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Field label="邮箱" htmlFor="login-email" error={fieldErrors.email}>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                autoComplete="username"
                autoFocus
                square
                required
              />
            </Field>

            <Field label="口令" htmlFor="login-password" error={fieldErrors.password}>
              <PasswordInput
                id="login-password"
                value={password}
                onChange={setPassword}
                placeholder="请输入口令"
                autoComplete="current-password"
                square
                required
              />
            </Field>

            {tab === 'password' && needsCode ? (
              <Field
                label="两步验证码"
                htmlFor="login-code"
                error={fieldErrors.code}
                hint="打开验证器 App，填入当前显示的 6 位数字"
              >
                <Input
                  id="login-code"
                  value={code}
                  onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  square
                  maxLength={6}
                  autoFocus
                />
              </Field>
            ) : null}

            {tab === 'recovery' ? (
              <Field
                label="恢复码"
                htmlFor="login-recovery"
                error={fieldErrors.recoveryCode}
                hint="恢复码只能使用一次；用完后请到「安全设置」重新生成一批"
              >
                <Input
                  id="login-recovery"
                  value={recoveryCode}
                  onChange={setRecoveryCode}
                  placeholder="XXXXX-XXXXX"
                  square
                  autoFocus
                />
              </Field>
            ) : null}

            {tab === 'password' ? (
              <Checkbox
                checked={remember}
                onChange={setRemember}
                label={<span className="text-fg-muted">记住我（30 天内免登录）</span>}
              />
            ) : null}

            {loginClosed ? (
              <Notice tone="warn">
                平台维护中，暂时无法登录。已登录的用户也已被下线，请稍后再试。
              </Notice>
            ) : null}

            {error ? (
              <p className="flex items-start gap-1.5 text-[13px] text-danger" role="alert">
                <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              block
              loading={busy}
              disabled={loginClosed}
              className="rounded-none"
            >
              登录
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between text-xs text-fg-muted">
            <Link to="/forgot-password" className="text-brand hover:underline">
              忘记口令？
            </Link>
            <span>
              还没有账号？
              {flags.registrationEnabled ? (
                <Link to="/register" className="ml-1 text-brand hover:underline">
                  立即注册
                </Link>
              ) : (
                <span className="ml-1 text-fg-subtle">暂未开放注册</span>
              )}
            </span>
          </div>

          <p className="mt-8 text-xs text-fg-subtle">
            <Link to="/" className="hover:text-brand">
              Havorix™ 控制台
            </Link>
            {' · console.havorix.cn'}
            {' · '}
            登录行为会被记录
          </p>
        </div>
      </section>
    </div>
  );
}
